import { SOURCE_SIDE } from "./constants.js"
import {
  activeVariants,
  activeMappings,
  compatibleOptionValues,
  mappedVariantMappings,
  unmappedVariants,
  variantOptionKey,
} from "./state.js"

export function selectionTemplate(side, product, state, loading = false, error = null) {
  if (loading) return '<div class="rounded-md border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground" role="status" aria-live="polite">Loading active variants…</div>'
  if (error) return `<div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-5 text-sm text-destructive" role="alert"><span>${escapeHtml(error)}</span><button type="button" class="ml-2 font-medium underline underline-offset-2" data-action="click->pages--mapping-form#retryProduct" data-mapping-side="${side}">Retry</button></div>`
  if (!product) return '<div class="rounded-md border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">Choose a product to identify its variant.</div>'

  const variants = activeVariants(product)
  if (variants.length === 0) return '<div class="rounded-md border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">This product has no active variants.</div>'

  const optionVariants = side === SOURCE_SIDE ? unmappedVariants(product) : variants
  const selectAll = side === SOURCE_SIDE && state.select_all !== false
  const groups = optionGroups(optionVariants)
  const options = selectAll ? "" : renderOptionGroups(side, product, state, groups, optionVariants)
  const status = renderVariantStatus(side, state)
  const hasAllVariantsMapping = activeMappings(product).some((mapping) => mapping.source_variant_id === null)
  const mappedSummary = side === SOURCE_SIDE && !hasAllVariantsMapping ? mappedCombinationsTemplate(product) : ""

  return `<div class="space-y-4" data-mapping-variant-options="${side}">${options}</div><div class="mt-4 text-sm" role="status" aria-live="polite" data-mapping-variant-status="${side}">${status}</div>${mappedSummary}`
}

export function productRowTemplate(product, side, selected = false) {
  const title = escapeHtml(product.title)
  const count = Number(product.active_variant_count || 0)
  const variantLabel = count === 0 ? "No variants" : `${count} ${count === 1 ? "variant" : "variants"}`
  const initials = product.title.split(/\s+/).map((word) => word[0]).join("").slice(0, 2).toUpperCase()
  const image = product.image_url
    ? `<img src="${escapeHtml(product.image_url)}" alt="" class="size-10 shrink-0 rounded-md border object-cover">`
    : `<div class="grid size-10 shrink-0 place-items-center rounded-md border bg-muted text-sm font-medium text-muted-foreground" aria-hidden="true">${escapeHtml(initials)}</div>`
  const selectedClasses = selected ? " border-primary bg-muted/50" : ""

  return `<button type="button" class="group flex min-h-16 w-full items-center gap-3 rounded-lg border border-transparent px-3 py-3 text-left transition-[border-color,background-color,box-shadow] duration-150 hover:border-border hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-pressed:border-primary/60 aria-pressed:bg-primary/5${selectedClasses}" aria-pressed="${selected ? "true" : "false"}" aria-label="Select ${title}" data-action="click->pages--mapping-form#selectProduct" data-mapping-product-id="${escapeHtml(product.id)}" data-mapping-side="${side}">${image}<div class="min-w-0 flex-1"><div class="truncate text-sm font-medium text-foreground">${title}</div><div class="mt-1 truncate text-xs text-muted-foreground">${variantLabel}</div></div></button>`
}

function renderOptionGroups(side, product, state, groups, optionVariants) {
  return Object.entries(groups).map(([name, values]) => `
      <fieldset class="space-y-2">
        <legend class="text-sm font-medium">${escapeHtml(name)}</legend>
        <div class="flex flex-wrap gap-2">
          ${values.filter((value) => compatibleOptionValues(optionVariants, state.options, name).includes(value)).map((value) => optionTemplate(side, state, name, value, optionVariants)).join("")}
        </div>
      </fieldset>`).join("")
}

function optionTemplate(side, state, name, value, optionVariants) {
  const selected = state.options[name] === value
  const compatible = compatibleOptionValues(optionVariants, state.options, name).includes(value)
  const mapped = false
  const badgeClasses = selected
    ? "text-primary bg-primary/5 ring-primary/20 hover:bg-primary/10"
    : "text-foreground bg-background ring-border hover:bg-muted"
  const disabled = !compatible || mapped

  return `
            <label class="inline-flex max-w-full ${disabled ? "cursor-not-allowed" : "cursor-pointer"} rounded-md focus-within:outline-none focus-within:ring-2 focus-within:ring-ring">
              <input class="sr-only peer" type="radio" name="${side}-${slugify(name)}" value="${escapeHtml(value)}" ${selected ? "checked" : ""} ${disabled ? "disabled" : ""} data-mapping-mapped="${mapped ? "true" : "false"}" data-action="change->pages--mapping-form#selectOption" data-mapping-side="${side}" data-mapping-option-name="${escapeHtml(name)}">
              <span class="inline-flex max-w-full items-center rounded-md font-medium ring-1 ring-inset px-3 py-1 text-xs transition-colors ${badgeClasses}">${escapeHtml(value)}</span>
            </label>`
}

function mappedCombinationsTemplate(product) {
  const entries = mappedVariantMappings(product)
  if (entries.length === 0) return ""

  const body = entries.map(({ variant, mapping }) => `
          <div data-mapping-combination="${escapeHtml(variantOptionKey(variant.option_values))}" data-mapping-id="${escapeHtml(mapping.id)}">
            <span class="relative inline-flex items-center rounded-md font-medium ring-1 ring-inset px-3 py-2 pr-12 text-xs text-primary bg-primary/5 ring-primary/20" data-mapping-combination-badge="true">
              <span class="truncate">${escapeHtml(variant.title)}</span>
              <button type="button" class="absolute right-0.5 inline-flex size-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Delete mapping for ${escapeHtml(variant.title)}" data-action="click->pages--mapping-form#openDeleteDialog" data-mapping-id="${escapeHtml(mapping.id)}" data-mapping-variant-id="${escapeHtml(variant.id)}">
                <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
              </button>
            </span>
          </div>`).join("")

  return `<div class="mt-4 rounded-lg border border-primary/20 bg-primary/5 px-3 py-3" data-mapping-mapped-options="${SOURCE_SIDE}" aria-label="Mapped combinations"><p class="text-xs font-semibold uppercase tracking-[0.12em] text-primary">Mapped combinations</p><div class="mt-2 flex flex-wrap gap-2">${body}</div></div>`
}

function optionGroups(variants) {
  const groups = {}
  variants.forEach((variant) => Object.entries(variant.option_values).forEach(([name, value]) => {
    groups[name] ||= new Set()
    groups[name].add(value)
  }))
  return Object.fromEntries(Object.entries(groups).map(([name, values]) => [name, [...values]]))
}

function renderVariantStatus(side, state) {
  if (side === SOURCE_SIDE && state.select_all !== false) {
    return '<div class="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100"><span class="mt-1 size-2 shrink-0 rounded-full bg-emerald-600 dark:bg-emerald-400" aria-hidden="true"></span><span class="font-medium">All active variants selected</span></div>'
  }

  if (state.variant) {
    return `<div class="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100"><span class="mt-1 size-2 shrink-0 rounded-full bg-emerald-600 dark:bg-emerald-400" aria-hidden="true"></span><span><span class="font-medium">Variant selected</span><span class="ml-2 text-emerald-800 dark:text-emerald-200">${escapeHtml(state.variant.title)}</span></span></div>`
  }

  return '<div class="rounded-lg border border-dashed bg-muted/20 px-3 py-2 text-sm text-muted-foreground">Choose options to show a variant.</div>'
}

function slugify(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
}

export function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character])
}
